import React, { useMemo } from "react";
import { Icon, Stack, Text } from "@fluentui/react";

interface RawIndicatorRow {
  sectionName?: string;
  metricName?: string;
  factorLabel?: string;
  value: string | number;
}

interface FeatureHealthProps {
  rows: RawIndicatorRow[];
}

// ─── data types ───────────────────────────────────────────────────────────────

interface FeatureRequestData {
  sectionTitle: string;
  score: number;
  p0Count: number;
  p1Count: number;
  impactPoints: number;
  bandText: string;
  formulaText: string;
  footerLine: string;
}

// ─── parser ───────────────────────────────────────────────────────────────────

const toNum = (v: string | number): number => {
  const n = Number(String(v).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};

function parseData(rows: RawIndicatorRow[]): FeatureRequestData {
  let score = 0, p0Count = 0, p1Count = 0, impactPoints = 0;
  let bandText = "", formulaText = "", footerLine = "";
  const sectionTitle = rows[0]?.sectionName ?? "Feature Request Indicator";

  for (const row of rows) {
    const metric = (row.metricName ?? "").trim();
    const factor = (row.factorLabel ?? "").trim();
    const val = String(row.value ?? "").trim();

    if (/^P0/i.test(metric)) {
      p0Count = toNum(row.value);
    } else if (/^P1/i.test(metric)) {
      p1Count = toNum(row.value);
    } else if (metric === "Score") {
      score = toNum(row.value);
    } else if (/^Impact Points/i.test(metric)) {
      impactPoints = toNum(row.value);
    } else if (/^Band/i.test(metric) || /^Band/i.test(factor)) {
      bandText = val || factor;
    } else if (/^Formula/i.test(metric) || /^Formula/i.test(factor)) {
      formulaText = val || factor;
    } else if (/^Band/i.test(val) || val.includes("Band:") || val.includes("Formula:")) {
      // Sometimes the whole band+formula line is in value
      footerLine = val;
    }
  }

  return { sectionTitle, score, p0Count, p1Count, impactPoints, bandText, formulaText, footerLine };
}

// ─── count box ────────────────────────────────────────────────────────────────

interface CountBoxProps {
  topLabel: string;
  value: number;
  bottomLabel: string;
  valueColor: string;
  borderColor: string;
  icon?: React.ReactNode;
}

const CountBox: React.FC<CountBoxProps> = ({ topLabel, value, bottomLabel, valueColor, borderColor, icon }) => (
  <Stack
    horizontalAlign="center"
    tokens={{ childrenGap: 4 }}
    styles={{
      root: {
        backgroundColor: "#FFFFFF",
        border: `1.5px solid ${borderColor}`,
        borderRadius: 10,
        padding: "14px 20px",
        flex: "1 1 0",
        minWidth: 110,
        textAlign: "center",
      },
    }}
  >
    <Stack horizontal verticalAlign="center" horizontalAlign="center" tokens={{ childrenGap: 4 }}>
      {icon}
      <Text variant="small" styles={{ root: { color: "#605E5C" } }}>{topLabel}</Text>
    </Stack>
    <Text
      styles={{
        root: {
          fontSize: 40,
          fontWeight: 700,
          color: valueColor,
          lineHeight: "44px",
        },
      }}
    >
      {value}
    </Text>
    <Text variant="small" styles={{ root: { color: "#605E5C" } }}>{bottomLabel}</Text>
  </Stack>
);

// ─── main export ──────────────────────────────────────────────────────────────

const IntuneFeatureHealthRawIndicatorCards: React.FC<FeatureHealthProps> = ({ rows }) => {
  const data = useMemo(() => parseData(rows), [rows]);

  // Build footer line from parts if not already combined
  const footer = data.footerLine
    || [data.bandText && `Band: ${data.bandText}`, data.formulaText && `Formula: ${data.formulaText}`]
        .filter(Boolean)
        .join(" · ");

  return (
    <Stack
      tokens={{ childrenGap: 16 }}
      styles={{
        root: {
          backgroundColor: "#F1FBF1",
          border: "1.5px solid #9ED89E",
          borderRadius: 12,
          padding: "20px 24px",
          maxWidth: 600,
        },
      }}
    >
      {/* Header */}
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
          <Icon iconName="PageList" styles={{ root: { fontSize: 20, color: "#107C10" } }} />
          <Text variant="large" styles={{ root: { fontWeight: 700, color: "#107C10" } }}>
            {data.sectionTitle}
          </Text>
        </Stack>
        {data.score > 0 && (
          <Stack
            horizontalAlign="center"
            verticalAlign="center"
            styles={{
              root: {
                backgroundColor: "#107C10",
                borderRadius: 8,
                padding: "4px 14px",
                minWidth: 60,
              },
            }}
          >
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 700, color: "#FFFFFF" } }}>
              {Number.isInteger(data.score) ? data.score : data.score.toFixed(2)}
            </Text>
          </Stack>
        )}
      </Stack>

      {/* Three count boxes */}
      <Stack horizontal tokens={{ childrenGap: 10 }} styles={{ root: { flexWrap: "wrap" } }}>
        <CountBox
          topLabel="P0 (Critical)"
          value={data.p0Count}
          bottomLabel="Feature Requests"
          valueColor="#D13438"
          borderColor="#F4B8BA"
        />
        <CountBox
          topLabel="P1 (Important)"
          value={data.p1Count}
          bottomLabel="Feature Requests"
          valueColor="#C15A00"
          borderColor="#F9D5A3"
        />
        <CountBox
          topLabel="Impact Points"
          value={data.impactPoints}
          bottomLabel="Customer Impact"
          valueColor="#1252A3"
          borderColor="#BDD7EE"
          icon={
            <Icon iconName="LightningBolt" styles={{ root: { fontSize: 13, color: "#FFB900" } }} />
          }
        />
      </Stack>

      {/* Footer band/formula */}
      {footer && (
        <>
          <div style={{ height: 1, backgroundColor: "#EDEBE9" }} />
          <Text variant="small" styles={{ root: { color: "#323130", lineHeight: "20px" } }}>
            {footer}
          </Text>
        </>
      )}
    </Stack>
  );
};

export default IntuneFeatureHealthRawIndicatorCards;
